import { useCallback, useMemo, useState } from 'react';
import moment from 'moment';
import { Form } from 'react-bootstrap';
import { Trans } from 'react-i18next';
import i18n from 'i18n';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { Modal } from 'components/common/Modal/Modal';
import { DeviceUnitSortOrder } from 'core/deviceManagement/DeviceManagement';
import { DeviceManagementFilter } from './deviceFilters';
import {
  buildExportFilterLabel,
  buildExportSortLabel,
  DEVICE_EXPORT_ERROR_I18N,
  DeviceExportScope,
  resolveExportLimit
} from './deviceExport';
import styles from './deviceManagement.module.scss';

type DeviceExportModalProps = {
  /** 目前條件下的總筆數，寫在「全部」那一項上。 */
  totalCount: number;
  /** 這次匯出實際會用的排序，與下面兩個條件都取自真的會送出去的那一份查詢。 */
  sortField: string;
  sortOrder: DeviceUnitSortOrder;
  filter: DeviceManagementFilter;
  search: string;
  /** 名單模式的檔名；沒有在名單模式時不給。 */
  listFileName?: string;
  /** 檔案還在下載中。 */
  exporting: boolean;
  /** 帶著筆數上限（undefined 表示全部）與備用檔名去呼叫匯出，下載完才關掉面板。 */
  onExport: (
    limit: number | undefined,
    fallbackFileName: string,
    onDownloaded: () => void
  ) => void;
  onClose: () => void;
  /** 這次匯出有什麼要先講清楚的，例如檔案裡不含畫面上的某幾欄。 */
  hint?: string;
};

// 正式檔名由後端的 Content-Disposition 決定，這裡組的只在讀不到那個 header 時才用得上。
// 因此帶時間戳：這條路徑上沒有條件可以寫進檔名，只剩時間分得開同一天的多次匯出。
const buildFallbackFileName = () =>
  `device_units_${moment().format('YYYYMMDDHHmm')}.csv`;

/**
 * 匯出清單。範圍分「全部」與「前 X 筆」，兩者都沿用畫面當下的篩選條件與排序，
 * 匯出的內容就與看到的清單一致。
 *
 * 面板上把排序與條件寫出來，是因為檔案本身看不出這兩件事：使用者匯出前改過條件、
 * 或在另一個分頁改過排序時，檔案裡的順序與筆數都會跟他印象中的不一樣。
 * 這兩行的來源與真的送出去的那一份查詢是同一個，不是另外算一份。
 */
export const DeviceExportModal: React.FC<DeviceExportModalProps> = ({
  totalCount,
  sortField,
  sortOrder,
  filter,
  search,
  listFileName,
  exporting,
  onExport,
  onClose,
  hint
}) => {
  const [scope, setScope] = useState<string>(DeviceExportScope.ALL);
  const [countInput, setCountInput] = useState('');
  // 輸入框碰過了沒。沒碰過就先跳一行紅字，等於在使用者還沒做錯任何事之前先罵他。
  const [countTouched, setCountTouched] = useState(false);

  // 驗證是算出來的，不是按下去才算：只在按下之後才說「數字不對」，等於按鈕在
  // 不能用的時候看起來仍然可以用。算在這裡，下載鈕才停得住。
  const resolution = useMemo(
    () => resolveExportLimit(scope, countInput),
    [scope, countInput]
  );
  const error = resolution.ok
    ? undefined
    : i18n.t(DEVICE_EXPORT_ERROR_I18N[resolution.reason]);

  const onScopeChange = useCallback((value: string) => {
    setScope(value);
    setCountTouched(false);
  }, []);

  const onCountChange = useCallback((event: any) => {
    setCountInput(event.target.value);
    setCountTouched(true);
  }, []);

  const download = useCallback(() => {
    if (!resolution.ok) {
      return;
    }
    // 下載完才關面板：關掉之後才開始下載的話，畫面上什麼都沒發生，
    // 使用者只會以為按鈕壞了。下載中的轉圈由 exporting 畫。
    onExport(resolution.limit, buildFallbackFileName(), onClose);
  }, [resolution, onExport, onClose]);

  return (
    <>
      {exporting && <LoadingIndicator />}
      <Modal
        title={i18n.t('deviceManagement.buttons.export')}
        dismiss={onClose}
        animation={false}
        primaryButton={{
          title: i18n.t('common.buttons.download'),
          disabled: error !== undefined || exporting,
          callback: download
        }}
        secondaryButton={{
          title: i18n.t('common.buttons.cancel'),
          callback: onClose
        }}
      >
        <div className={styles.exportContent}>
          <div className={styles.exportLabel}>
            {i18n.t('deviceManagement.labels.exportScope')}
          </div>
          <Form.Check
            type='radio'
            id='deviceExportScopeAll'
            name='deviceExportScope'
            label={i18n.t('deviceManagement.labels.exportAll', {
              total: totalCount
            })}
            checked={scope === DeviceExportScope.ALL}
            onChange={() => onScopeChange(DeviceExportScope.ALL)}
          />
          <Form.Check
            type='radio'
            id='deviceExportScopeTopN'
            name='deviceExportScope'
            label={i18n.t('deviceManagement.labels.exportTopN')}
            checked={scope === DeviceExportScope.TOP_N}
            onChange={() => onScopeChange(DeviceExportScope.TOP_N)}
          />
          {scope === DeviceExportScope.TOP_N && (
            <div className={styles.exportCount}>
              <Form.Control
                type='number'
                min={1}
                value={countInput}
                onChange={onCountChange}
                aria-label={i18n.t('deviceManagement.labels.exportCount')}
              />
            </div>
          )}
          <div className={styles.exportSummary} data-testid='export-summary'>
            <div>
              {/* 只有排序值上色，前面那個「依」不上色，所以走 Trans 而不是整句 t()。 */}
              <Trans
                i18nKey='deviceManagement.labels.exportSortBy'
                tOptions={{ sort: buildExportSortLabel(sortField, sortOrder) }}
              >
                ...<span className={styles.exportSortValue}>...</span>
              </Trans>
            </div>
            <div>{buildExportFilterLabel(filter, search, listFileName)}</div>
            <div className={styles.exportHint}>
              {i18n.t('deviceManagement.hints.exportConditionsInFileName')}
            </div>
          </div>
          {hint && (
            <div className={styles.exportHint} data-testid='export-hint'>
              {hint}
            </div>
          )}
          {error !== undefined && countTouched && (
            <div className={styles.exportError} data-testid='export-error'>
              {error}
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};
